import { parseOrThrow } from '@shared/utils/validation-error';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { z } from 'zod';
import { db } from '@/firebase/config';
import type { CompanyProfile, ID } from '@shared/types';
import { buildBaseFields, buildUpdateFields, fromSnapshot, writeAuditLog } from '@shared/utils';

const companyProfileSchema = z.object({
  name: z.string().min(1, '会社名は必須です'),
  zip: z.string().nullable().default(null),
  prefecture: z.string().nullable().default(null),
  addr1: z.string().nullable().default(null),
  addr2: z.string().nullable().default(null),
  addr3: z.string().nullable().default(null),
  phone: z.string().nullable().default(null),
  fax: z.string().nullable().default(null),
  registrationNumber: z.string().nullable().default(null),
  logoUrl: z.string().nullable().default(null),
  stampUrl: z.string().nullable().default(null),
});

export type CompanyProfileInput = z.infer<typeof companyProfileSchema>;

const COL = 'company_profile';
// 自社情報は単一レコード運用のため固定IDで保存する。
const DOC_ID = 'main';

export async function fetchCompanyProfile(): Promise<CompanyProfile | null> {
  const snap = await getDoc(doc(db, COL, DOC_ID));
  return fromSnapshot<CompanyProfile>(snap);
}

export async function saveCompanyProfile(
  data: CompanyProfileInput,
  userId: ID
): Promise<CompanyProfile> {
  const validated = parseOrThrow(companyProfileSchema, data);
  const ref = doc(db, COL, DOC_ID);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    const before = snap.data();
    const patch = { ...validated, ...buildUpdateFields(userId) };
    await updateDoc(ref, patch);
    await writeAuditLog({
      entityType: 'company_profile',
      entityId: DOC_ID,
      action: 'update',
      userId,
      before,
      after: validated,
    });
    return { id: DOC_ID, ...before, ...patch } as unknown as CompanyProfile;
  }
  const payload = { ...validated, ...buildBaseFields(userId) };
  await setDoc(ref, payload);
  await writeAuditLog({
    entityType: 'company_profile',
    entityId: DOC_ID,
    action: 'create',
    userId,
    after: validated,
  });
  return { id: DOC_ID, ...payload } as unknown as CompanyProfile;
}
